/**
 * MUI NavSection Component
 * 
 * This component is the Material UI equivalent of the original NavSection component.
 * It renders a navigation section with lines for the Room Designer application.
 */

import React, { useRef, useLayoutEffect, useState, useContext } from 'react';
import { Box, useTheme as useMuiTheme, alpha } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTheme } from 'theme/ThemeContext';
import ToolContext from 'components/services/ToolContext';
import { ComponentsIndex } from 'globalConstants/ComponentIndex';
import { getConditionResult } from 'reduxModules/selectors/containerSelectors';
import { isInError } from 'reduxModules/ducks/ui';
import { MuiHyperlink as Hyperlink, MuiError as Error } from 'components/atoms/PlainText';
import { NavSectionProps } from 'types/components/organisms';
import type { RootState } from 'types/redux/state';

/**
 * Extended NavSectionProps interface to match the actual component usage
 */
interface MuiNavSectionProps extends Omit<NavSectionProps, 'children' | 'title'> {
  item?: string;
}

/**
 * Interface for SectionBody props
 */
interface SectionBodyProps {
  setHeight: (height: number) => void;
  lines?: Array<LineProps>;
  dispatcher?: string;
  mode?: string;
  muiTheme?: any;
}

/**
 * Interface for Line props
 */
interface LineProps {
  name?: string;
  dispatcher?: string;
  condition?: Record<string, any>;
  element?: string;
  label?: string;
  value?: any;
  infoLink?: string;
  infoText?: string;
  error?: string;
  errorCode?: number;
  animate?: boolean;
  mode?: string;
  muiTheme?: any;
  [key: string]: any; // For additional props passed to the element
}


/**
 * MUI NavSection component
 * 
 * @param props - Component props
 * @returns The MUI NavSection component
 */
const MuiNavSection: React.FC<MuiNavSectionProps> = (props) => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme();
  const sectionName = props.item;
  
  // Type-safe useSelector hooks
  const activeTab = useSelector((state: RootState) => state.container.activeTab);
  const hasBarMicrophone = useSelector((state: RootState) => {
    const micModel = state.container.areas[activeTab]?.microphones?.model;
    return micModel === 'bar' || micModel === 'audiobar' || micModel === 'videobar';
  });
  
  // Check if it's specifically the VBC video bar (which has integrated camera)
  const hasVideoBar = useSelector((state: RootState) => {
    const microphones = state.container.areas[activeTab]?.microphones;
    return microphones?.model === 'bar' && microphones?.modelName === 'Parlé VBC 2500a';
  });
  
  // Only prevent camera expansion for VBC video bar (has integrated camera)
  // ABC audio bar needs camera selector, so don't prevent expansion
  const shouldPreventExpansion = hasBarMicrophone && sectionName === 'speakers' ||
    (hasVideoBar && sectionName === 'camera');
  
  const active = useSelector((state: RootState) => 
    state.ui.currentView === sectionName?.replace('_', '')
  ) && !shouldPreventExpansion;
  
  const showing = useSelector((state: RootState) => state.ui.showing);
  
  // We need to cast ToolContext to any since we don't have its type definition yet
  const toolContext = useContext(ToolContext) as any;
  const lines = toolContext.menus[sectionName as string]?.[showing];

  const [height, setHeight] = useState<number>(0);

  return (
    <Box 
      className={`ms-2 mb-${active ? 2 : 0}`}
      sx={{
        overflow: active ? 'auto' : 'hidden',
        maxHeight: active ? '800px' : '0px',
        transition: 'max-height 0.3s ease-in-out',
        display: 'flex',
        marginLeft: 0,  // Remove extra margin since parent provides padding
        paddingX: 0     // Remove extra padding since parent provides padding
      }}
    >
      <SectionBody setHeight={setHeight} lines={lines} mode={mode} muiTheme={muiTheme} />
    </Box>
  );
};

/**
 * SectionBody component
 * 
 * @param props - Component props
 * @returns The SectionBody component
 */
const SectionBody: React.FC<SectionBodyProps> = (props) => {
  const { setHeight, mode, muiTheme } = props;
  const childRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (childRef.current) {
      const { height } = childRef.current.getBoundingClientRect();
      setHeight(height);
    }
  }, [setHeight]);
  
  return (
    <Box 
      ref={childRef} 
      className="vstack pb-2"
      sx={{
        fontSize: '14px',
        color: muiTheme?.palette.text.primary || (mode === 'dark' ? '#ffffff' : '#333333'),
        width: '100%'
      }}
    >
      {props.lines ? (
        props.lines.map((line, index) => {
          const lineProps = {
            ...line,
            dispatcher: line.dispatcher || props.dispatcher,
            mode,
            muiTheme,
          };
          return <Line key={`${line.name}-${index}`} {...lineProps} />;
        })
      ) : (
        <Box sx={{ color: muiTheme?.palette.text.secondary || '#b0b0b0', fontSize: '14px' }}>This section has not been created yet</Box>
      )}
    </Box>
  );
};

/**
 * Line component
 * 
 * @param props - Component props
 * @returns The Line component
 */
const Line: React.FC<LineProps> = (props) => {
  const { mode, muiTheme, ...restProps } = props;
  
  const visible = useSelector((state: RootState) => 
    // Cast getConditionResult to any as we don't have its exact type definition
    (getConditionResult as any)(state, { ...props.condition })
  );
  
  // Check for validation errors from Redux
  const hasValidationError = useSelector((state: any) => {
    if (!props.errorCode) return false;
    
    // For dimensionInput, check if any of the field parameters are in error
    if (props.element === 'dimensionInput' && props.ids) {
      return props.ids.some((fieldName: string) => 
        (isInError as any)(state, { 
          errorCode: props.errorCode, 
          name: fieldName 
        })
      );
    }
    
    // For other inputs, check the specific field name
    return (isInError as any)(state, { 
      errorCode: props.errorCode, 
      name: props.name 
    });
  });

  // Get the error message from Redux error state
  const validationErrorMessage = useSelector((state: any) => {
    if (!props.errorCode || !hasValidationError) return null;
    const error = state.ui.error[props.errorCode];
    return error?.msg || null;
  });
  
  const Element = ComponentsIndex[props.element];

  const activeTab = useSelector((state: RootState) => state.container.activeTab);
  const hasVideoBar = useSelector((state: RootState) => 
    state.container.areas[activeTab]?.microphones?.model === 'videobar'
  );

  // Determine which error message to show (manual error prop takes precedence)
  const errorMessage = props.error || validationErrorMessage;

  // Microphone coverage overlap errors are now handled by toast warnings in calculateDevicesMiddleware
  // No need for useEffect here since coverage overlaps trigger toasts when they occur
  const isMicrophoneCoverageError = false; // Always false since we handle this at the middleware level

  if (props.label === 'Model' && hasVideoBar) {
    return null;
  }

  // If Element is not found, don't render anything to prevent errors
  if (!Element || !props.element) {
    return null;
  }

  return (
    <Box 
      className={`menuLine mb-${visible ? 2 : 0}`}
      sx={{
        overflow: 'hidden',
        maxHeight: visible ? 'fit-content' : '0px',
        transition: props.animate ? 'max-height .3s ease-out' : 'none',
        '& .MuiInputBase-root': {
          fontSize: '14px',
        },
      }}
    >
      <Element {...restProps} isNav dispatcher={restProps.dispatcher} value={restProps.value} />
      {restProps.infoLink && <Hyperlink link={restProps.infoLink} msg={restProps.infoText} isNav={true} />}
      {/* Show inline error for non-microphone coverage errors */}
      {errorMessage && !isMicrophoneCoverageError &&
       <Error msg={errorMessage} isNav={true} />}
    </Box>
  );
};

export default MuiNavSection;
